/**
 * The scale a measure's value is stored at (Nexo-Formula-Language.md §9.2). A measure bound to a PlanLine column is
 * stored at that column's scale — PlanLine.yaml's `scale`, generated as `PlanLineScales` — and a driver at
 * PlanDriverLine.value's (`PlanDriverLineScales`); the server takes an input at that scale before it derives anything,
 * and stores a derived amount at the amount's (PlanLineColumnScales.cs), rounded half away from zero as PostgreSQL
 * rounds a numeric. The entry grid keeps what it accepts — typed, pasted or spread — to the same scale, so
 * a cell shows what the line will hold and the grid derives from the values the server derives from.
 */
import { PlanLineScales } from '@/types/finance/PlanLine';
import { PlanDriverLineScales } from '@/types/finance/PlanDriverLine';
import type { MeasureLike } from './planEntryRows';

/** The PlanLine column each binding stores a value in — the server's routing (RouteMeasures). */
const PLAN_LINE_COLUMN: Readonly<Record<string, keyof typeof PlanLineScales>> = {
  PLANLINE_QUANTITY: 'quantity',
  PLANLINE_UNITPRICE: 'unitPrice',
  PLANLINE_AMOUNT: 'amount',
};

/** The decimal places a measure's value is stored with; null for a measure stored nowhere (computed, engine-resolved). */
export function storedScale(measure: Pick<MeasureLike, 'storageBinding'>): number | null {
  if (measure.storageBinding === 'DRIVER') return PlanDriverLineScales.value;
  const column = measure.storageBinding ? PLAN_LINE_COLUMN[measure.storageBinding] : undefined;
  return column ? PlanLineScales[column] : null;
}

export { atScale, fitsScale } from '@/lib/decimalScale';
